import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { formatCurrency, getMonthName, getCurrentYear } from '../lib/utils';
import { Calculator, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { ExportButtons } from '../components/ExportButtons';

export function TVAPage() {
  const { api } = useAuth();
  const [data, setData] = useState([]);
  const [year, setYear] = useState(getCurrentYear());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [year]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/tva', { params: { annee: year } });
      setData(res.data);
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setLoading(false);
    }
  };

  const totalCollectee = data.reduce((sum, d) => sum + d.tva_collectee, 0);
  const totalDeductible = data.reduce((sum, d) => sum + d.tva_deductible, 0);
  const totalAPayer = data.reduce((sum, d) => sum + d.tva_a_payer, 0);

  return (
    <div className="space-y-6">
      <div className="module-header">
        <div>
          <h1 className="module-title">TVA</h1>
          <p className="text-muted-foreground mt-1">Suivi de la TVA collectée, déductible et à payer</p>
        </div>
        <div className="flex items-center gap-3">
          <ExportButtons api={api} moduleKey="tva" year={year} />
          <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026, 2027, 2028, 2029, 2030].map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-green-600" />
              TVA Collectée
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold tabular-nums text-green-600">{formatCurrency(totalCollectee)}</p>
            <p className="text-xs text-muted-foreground mt-1">Sur ventes + commissions plateformes</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-blue-600" />
              TVA Déductible
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold tabular-nums text-blue-600">{formatCurrency(totalDeductible)}</p>
            <p className="text-xs text-muted-foreground mt-1">Sur dépenses réelles</p>
          </CardContent>
        </Card>

        <Card className={totalAPayer > 0 ? 'border-amber-300 bg-amber-50/50 dark:bg-amber-900/10' : 'border-green-300 bg-green-50/50 dark:bg-green-900/10'}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground flex items-center gap-2">
              <Calculator className="w-4 h-4" />
              TVA à Payer
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className={`text-3xl font-bold tabular-nums ${totalAPayer > 0 ? 'text-amber-600' : 'text-green-600'}`}>
              {formatCurrency(totalAPayer)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {totalAPayer > 0 ? 'À reverser à l\'État' : 'Crédit de TVA'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Table */}
      <Card>
        <CardHeader>
          <CardTitle>Détail mensuel {year}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Mois</TableHead>
                <TableHead className="text-right">TVA Collectée</TableHead>
                <TableHead className="text-right">TVA Déductible</TableHead>
                <TableHead className="text-right">TVA à Payer</TableHead>
                <TableHead className="text-center">Statut</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map(item => (
                <TableRow key={item.mois}>
                  <TableCell className="font-medium">{getMonthName(item.mois)}</TableCell>
                  <TableCell className="text-right tabular-nums text-green-600">
                    {formatCurrency(item.tva_collectee)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-blue-600">
                    {formatCurrency(item.tva_deductible)}
                  </TableCell>
                  <TableCell className={`text-right tabular-nums font-medium ${item.tva_a_payer > 0 ? 'text-amber-600' : item.tva_a_payer < 0 ? 'text-green-600' : ''}`}>
                    {formatCurrency(item.tva_a_payer)}
                  </TableCell>
                  <TableCell className="text-center">
                    {item.tva_collectee === 0 && item.tva_deductible === 0 ? (
                      <span className="text-muted-foreground text-sm">-</span>
                    ) : item.tva_a_payer > 0 ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-800 rounded-full text-xs">
                        <TrendingUp className="w-3 h-3" /> À payer
                      </span>
                    ) : item.tva_a_payer < 0 ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                        <TrendingDown className="w-3 h-3" /> Crédit
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs">
                        <Minus className="w-3 h-3" /> Neutre
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {/* Total row */}
              <TableRow className="bg-muted/50 font-medium">
                <TableCell>TOTAL {year}</TableCell>
                <TableCell className="text-right tabular-nums text-green-600">{formatCurrency(totalCollectee)}</TableCell>
                <TableCell className="text-right tabular-nums text-blue-600">{formatCurrency(totalDeductible)}</TableCell>
                <TableCell className={`text-right tabular-nums ${totalAPayer > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                  {formatCurrency(totalAPayer)}
                </TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
